import { ethers } from "ethers";
import { EVM_CONFIG } from "../config";
import { requestPrivateFees } from "../utils/relayer-client";
import {
  decryptInboxMessage,
  getInboxSecretKeyCandidates,
  getInboxViewingKeyCandidates,
} from "../utils/inbox-crypto";
import { formatMpkHex } from "../utils/format";
import type { SpendingKeys } from "../joinsplit/types";
import type { BaseHistoryEntry } from "../base-app/types";
import { privatePools, privatePoolForToken } from "./privatePools";
import { resolveTokenInfo } from "../base-app/helpers/tokenCatalog";
import { recoveryErrorMessage } from "../utils/recovery-error";

/** History reads confirmed receipts independently of unspent-note recovery.
 * Decrypted amounts/roles are display metadata, never spend authorization. */
export async function fetchPrivateFeeHistoryPage(
  keys: SpendingKeys,
  before: number | null = null,
  authHeaders: Record<string, string>,
): Promise<{ entries: BaseHistoryEntry[]; nextCursor: number | null }> {
  const pool = process.env.REACT_APP_PRIVATE_FEE_POOL;
  if (
    !pool ||
    pool.toLowerCase() !==
      EVM_CONFIG.CONTRACTS.SHIELDED_POOL_JOINSPLIT.toLowerCase()
  )
    return { entries: [], nextCursor: null };
  const page = await requestPrivateFees<{
    entries: any[];
    nextCursor: number | null;
  }>("history", { before, limit: 25 }, { authHeaders });
  if (
    !Array.isArray(page.entries) ||
    page.entries.length > 25 ||
    (page.nextCursor !== null &&
      (!Number.isSafeInteger(page.nextCursor) ||
        page.nextCursor < 1 ||
        (before !== null && page.nextCursor >= before)))
  )
    throw Error("Fee activity is unavailable");
  const entries: BaseHistoryEntry[] = [];
  for (const row of page.entries) {
    const note = await decryptInboxMessage<any>(
      row.envelope,
      getInboxViewingKeyCandidates(keys.spendingKey),
      formatMpkHex(keys.mpk),
      getInboxSecretKeyCandidates(keys.spendingKey),
    ).catch((error: unknown) => {
      throw new Error(
        `Fee activity record could not be opened: ${recoveryErrorMessage(error)}`,
      );
    });
    const role = note.privateFee?.role;
    if (
      note.schemaVersion !== 2 ||
      note.protocol !== "flat-joinsplit-v2" ||
      note.chainId !== EVM_CONFIG.CHAIN_ID ||
      !privatePools().some(
        (p) => p.toLowerCase() === note.poolAddress?.toLowerCase(),
      ) ||
      note.privateFee?.version !== 1 ||
      !["protocol", "partner", "relayer"].includes(role) ||
      !/^0x[0-9a-f]{64}$/i.test(row.commitment) ||
      note.commitment?.toLowerCase() !== row.commitment.toLowerCase() ||
      !ethers.isAddress(note.tokenData?.tokenAddress || "") ||
      note.tokenData?.tokenType !== 0 ||
      note.tokenData?.tokenSubID !== "0" ||
      typeof note.value !== "string" ||
      !/^[1-9][0-9]*$/.test(note.value) ||
      !/^0x[0-9a-f]{64}$/i.test(row.transactionHash) ||
      !Number.isSafeInteger(row.timestamp) ||
      row.timestamp <= 0
    )
      throw Error("Fee activity could not be verified");
    if (
      privatePoolForToken(note.tokenData.tokenAddress).toLowerCase() !==
      note.poolAddress.toLowerCase()
    )
      throw Error("Fee activity asset mismatch");
    const token = await resolveTokenInfo(note.tokenData.tokenAddress);
    entries.push({
      id: row.position,
      eventId: `fee:${note.chainId}:${note.poolAddress.toLowerCase()}:${row.commitment.toLowerCase()}`,
      type: "fee",
      feeRole: role,
      amount: ethers.formatUnits(note.value, token.decimals),
      symbol: token.symbol,
      txHash: row.transactionHash,
      timestamp: row.timestamp,
    });
  }
  return { entries, nextCursor: page.nextCursor };
}
